/**
 * Shared pieces of the per-TLD price grids (owner, 2026-09-29: "hiển thị luôn toàn bộ nghiệp vụ
 * để nhập giá 1 lần"): one row per operation, the current price and the new setting side by side,
 * saved in one batch request. On a phone each row becomes a small card with labelled fields.
 */
import type { ReactNode } from 'react';
import { isApiError } from '../../api';
import { cn } from '@/lib/utils';

const DIGITS: Record<string, number> = { VND: 0, JPY: 0, KRW: 0 };

/** A computed amount as a decimal string in the currency's minor units. */
export function roundMoney(v: number, currency: string): string {
  const d = DIGITS[currency] ?? 2;
  return (Math.round(v * 10 ** d) / 10 ** d).toFixed(d);
}

export const isDecimal = (v: string) => /^-?\d+(\.\d+)?$/.test(v.trim());

/** Typed decimals: "1.234.567" / "1,5" → "1234567" / "1.5" is left to the operator; only "," → ".". */
export const decimalInput = (v: string) => v.replace(',', '.').replace(/\s/g, '');

/** `details.rows` of a refused batch, keyed by the key of the row sent at that index. */
export function rowErrors(error: unknown, keys: string[]): Record<string, string> {
  if (!isApiError(error)) return {};
  const rows = (error.details as { rows?: { index: number; message: string }[] } | null)?.rows ?? [];
  const out: Record<string, string> = {};
  for (const r of rows) {
    const k = keys[r.index];
    if (k !== undefined) out[k] = r.message;
  }
  return out;
}

/** Column headers (wide screens only; on a phone each field carries its own label). */
export function GridHeader({ cols, template, alignLast = true }: { cols: ReactNode[]; template: string; alignLast?: boolean }) {
  return (
    <div className={cn('hidden gap-3 border-b px-3 pb-1.5 text-xs font-semibold tracking-wide text-muted-foreground uppercase sm:grid', template)} aria-hidden="true">
      {cols.map((c, i) => (
        // eslint-disable-next-line react/no-array-index-key -- fixed columns
        <span key={i} className={alignLast && i === cols.length - 1 ? 'text-right' : undefined}>
          {c}
        </span>
      ))}
    </div>
  );
}

/** One operation's row. */
export function GridRow({ template, children, focus, error, label }: { template: string; children: ReactNode; focus?: boolean; error?: string; label: string }) {
  return (
    <div
      role="group"
      aria-label={label}
      data-focus={focus ? 'true' : undefined}
      className={cn('grid gap-x-3 gap-y-2 rounded-md border px-3 py-2 sm:items-center sm:rounded-none sm:border-0 sm:border-b sm:last:border-b-0', template, focus && 'bg-primary/5 ring-1 ring-primary/40 sm:rounded-md', error && 'bg-critical-soft')}
    >
      {children}
      {error ? (
        <p className="text-xs text-critical sm:col-span-full" role="alert">
          {error}
        </p>
      ) : null}
    </div>
  );
}

/** A field of a row: labelled on a phone, bare in the wide grid. */
export function GridCell({ label, children, className }: { label: string; children: ReactNode; className?: string }) {
  return (
    <div className={cn('grid min-w-0 grid-cols-[7rem_minmax(0,1fr)] items-center gap-2 sm:block', className)}>
      <span className="text-xs text-muted-foreground sm:hidden">{label}</span>
      <div className="min-w-0">{children}</div>
    </div>
  );
}

/** The dialog width for a grid. */
export const GRID_DIALOG = 'xl' as const;
